import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PickerOptions, PickerResponse, Client } from 'filestack-js';
import type { PickerFileCallback, PickerFileErrorCallback, PickerFileMetadata, PickerInstance, PickerTransformationOptions } from 'filestack-js/build/main/lib/picker';
import type { LoadableLibrary } from '@loadable/component';
import { loadable } from '@shared/core/loadableFromNamedExport';

import { v1 as uuid } from 'uuid';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useFilestackLazyQuery } from '@graphql/queries/filestack.graphql.generated';
import { createContext } from '../createContext';
import { useImmer } from 'use-immer';
import type { ExecuteWithLibrary } from '../executeWithDeferredLibrary';
import { executeWithDeferredLibrary } from '../executeWithDeferredLibrary';
import { useEventCallback } from '../hooks/useEventCallback';
import { isChunkLoadError } from '../isChunkLoadError';
import { addError } from '../logger';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';
import { processUploadedFiles } from './pixelGuard';
import { pick } from 'lodash-es';

type LoadableModule<Module> = Module extends LoadableLibrary<infer R> ? R : never;

const FilestackLib = loadable.lib(
  () =>
    import(
      /* webpackChunkName: "static/webpack/vendor/filestack-js" */
      'filestack-js'
    ),
  {
    ssr: false,
  },
);

type LoadableFilestack = LoadableModule<typeof FilestackLib>;

type ContextType = {
  withClient: ExecuteWithLibrary<Client>;
  prepareForUse: () => void;
  loading: boolean;
};
const [Provider, useContext] = createContext<ContextType>({ name: 'Filestack' });

export const FilestackProvider: React.FC<{}> = ({ children }) => {
  const [{ hasInitializedClient, hasRequestedUsage, hasLoadedLibrary }, setState] = useImmer<{
    hasInitializedClient: boolean;
    hasLoadedLibrary: boolean;
    hasRequestedUsage: boolean;
  }>(() => ({
    hasInitializedClient: false,
    hasLoadedLibrary: false,
    hasRequestedUsage: false,
  }));
  const [loadError, setLoadError] = useState<Error | null>(null);
  const filestackRef = useRef<{ filestack: LoadableModule<typeof FilestackLib> | null; client: Client | undefined }>({ filestack: null, client: undefined });
  const [requestFilestackData, { data, loading }] = useFilestackLazyQuery();
  const deferredRef = useRef(executeWithDeferredLibrary<Client>());
  const rejectDeferred = deferredRef.current[2];

  const handleLibraryLoaded = useCallback<(instance: LoadableFilestack | null) => void>(
    instance => {
      filestackRef.current.filestack = instance;
      setState(draft => {
        draft.hasLoadedLibrary = !!instance;
      });
    },
    [setState],
  );

  const handleChunkError = useEventCallback((error: Error) => {
    setLoadError(error);
    rejectDeferred(error);
  });

  const prepareForUse = useCallback(() => {
    if (!hasRequestedUsage) {
      setState(draft => {
        draft.hasRequestedUsage = true;
      });
      requestFilestackData();
    }
  }, [hasRequestedUsage, requestFilestackData, setState]);

  const initClient = useCallback(() => {
    const filestack = filestackRef.current.filestack;
    if (data && filestack) {
      const client = filestack.init(data.filestack.apiKey, {
        security: {
          policy: data.filestack.policy,
          signature: data.filestack.signature,
        },
      });

      filestackRef.current.client = client;
      const [resolveClient] = deferredRef.current;
      resolveClient(client);

      setState(draft => {
        draft.hasInitializedClient = true;
      });
    }
  }, [data, setState]);

  const withClient = useCallback<ContextType['withClient']>(
    cb => {
      if (loadError) {
        return Promise.reject(loadError);
      }
      if (!hasRequestedUsage) {
        prepareForUse();
      }
      const executeWithClient = deferredRef.current[1];
      return executeWithClient(cb);
    },
    [loadError, hasRequestedUsage, prepareForUse],
  );

  // Init the client when ready
  const shouldInitClient = !hasInitializedClient && hasLoadedLibrary && !!data;
  useEffect(() => {
    if (shouldInitClient) {
      initClient();
    }
  }, [shouldInitClient, initClient]);

  const value = useMemo(() => {
    return { loading, withClient, prepareForUse };
  }, [loading, withClient, prepareForUse]);

  return (
    <>
      <Provider value={value}>{children}</Provider>
      <ChunkErrorBoundary
        section="shared/filestack"
        fallback={null}
        onError={handleChunkError}
      >
        {hasRequestedUsage && <FilestackLib ref={handleLibraryLoaded} />}
      </ChunkErrorBoundary>
    </>
  );
};

/**
 * Upload activity of the picker session being closed. Reported to `onClose` so
 * consumers can tell a plain cancel apart from a close where files were
 * selected or uploaded but nothing reached their `onUploadDone` (e.g. dropped
 * by the pixel guard or a complete upload failure).
 */
export type FilestackCloseInfo = Readonly<{
  /** A file was selected during the session (any source). */
  didSelectFiles: boolean;
  /** Filestack completed an upload during the session, whether or not it was forwarded to the consumer. */
  didUpload: boolean;
  /**
   * The upload was forwarded to the consumer's `onUploadDone`. `didUpload &&
   * !didForward` means the upload was dropped by validation or a complete
   * failure — the user was already notified via toast/callback.
   */
  didForward: boolean;
}>;

type Props = Readonly<{
  accept?: string | string[];
  containerId: string;
  onFileSelected?: PickerFileCallback;
  onUploadDone: (res: PickerResponse) => void | Promise<void>;
  onFileUploadFailed?: PickerFileErrorCallback;
  onCompleteUploadFailure?: () => void;
  maxFiles?: number;
  transformations?: PickerTransformationOptions;
  pickerContainer?: string | Node;
  rootId?: string;
  onOpen?: (handle: PickerInstance) => void;
  onClose?: (info: FilestackCloseInfo) => void;
  /**
   * The context for using the photo uploader
   */
  usageContext?: string;
  /**
   * Skips the on-mount warm-up (credentials fetch + `filestack-js` chunk). Set this
   * where the hook mounts on surfaces the user may never upload from — otherwise every
   * such page load pays for a picker that never opens.
   */
  deferPreparation?: boolean;
}>;

interface PreviewProps {
  url: string;
}
// When requesting assets from google drive, parenthese get encoded in the filename sent to filestack, we need to encode as well
const encodeForGoogleDrive = (filename: string) => encodeURIComponent(filename).replace(/\(/g, '%28').replace(/\)/g, '%29');

const defaultOnFileSelected: PickerFileCallback = async res => {
  // replace ~ with - in filename, because file savings api were failing when file name contains ~
  const fileName = res.filename.replace(/~/g, '-');
  return { ...res, filename: `${uuid()}-` + (res.source === 'googledrive' ? encodeForGoogleDrive(fileName) : fileName) };
};

/**
 * Eagerly triggers Filestack initialization (library chunk download +
 * credentials GQL query) when {@link condition} becomes `true`.
 *
 * Use this for preloading on routes that will need Filestack shortly after mount.
 */
export const usePrepareFilestackForUse = (condition: boolean) => {
  const { prepareForUse } = useContext();
  useEffect(() => {
    if (condition) {
      prepareForUse();
    }
  }, [condition, prepareForUse]);
};

/**
 * Explicitly extract known fields to avoid leaking user data.
 */
const extractPickerFileMetadata = (fileMetadata: PickerFileMetadata) => {
  return pick(fileMetadata, ['filename', 'handle', 'mimetype', 'cropped', 'rotated', 'originalPath', 'container', 'source', 'status', 'uploadId', 'url', 'size']);
};

/**
 * Upload activity for one picker session. Filestack can fire `onUploadDone` and
 * `onClose` at virtually the same instant (URL source; local files right after
 * bytes finish), while `wrappedOnUploadDone` awaits a network round-trip (the
 * pixel guard) before forwarding to the consumer. The close report must
 * therefore wait on the upload instead of guessing with a timer, or the
 * consumer observes "closed, nothing uploaded" and silently drops the upload
 * (PRNT-4630).
 *
 * Every picker callback is bound to the session its instance was opened with,
 * so a reopen mid-flight can never attribute one session's upload to another.
 */
type UploadSession = {
  processing: Promise<void> | null;
  /**
   * Armed once an upload is under way, resolved when the upload reports (or is
   * cancelled/abandoned), so a close that fired first can wait for it.
   */
  awaitUpload: { promise: Promise<void>; settle: () => void } | null;
  didSelectFiles: boolean;
  didUpload: boolean;
  didForward: boolean;
};

const createUploadSession = (): UploadSession => ({ processing: null, awaitUpload: null, didSelectFiles: false, didUpload: false, didForward: false });

/**
 * Upper bound on how long a close report waits for a late `onUploadDone`. Only
 * reached when an upload started and never reported at all — a user cancelling
 * settles the wait immediately via `onCancel`, and so does a reopen.
 */
export const UPLOAD_GRACE_MS = 10_000;

/** Idempotent: the picker can report an upload starting more than once. */
const armAwaitUpload = (session: UploadSession) => {
  if (session.awaitUpload) {
    return;
  }
  let settle!: () => void;
  const promise = new Promise<void>(resolve => {
    settle = resolve;
  });
  session.awaitUpload = { promise, settle };
};

const settleAwaitUpload = (session: UploadSession) => {
  session.awaitUpload?.settle();
};

export const useFilestack = (props: Props) => {
  const {
    containerId,
    onFileSelected,
    onUploadDone,
    onFileUploadFailed,
    onCompleteUploadFailure,
    onClose,
    onOpen,
    maxFiles = 50,
    pickerContainer,
    rootId,
    transformations = {},
    accept,
    usageContext,
    deferPreparation = false,
  } = props;
  const { withClient, prepareForUse } = useContext();
  const { toast } = useToast();
  const { tq } = useTranslation('sharedComponents');
  const oversizedImageError = tq`This image is too large to upload. Please try a smaller photo.`;
  const uploadFailedError = tq`We couldn't upload your photo. Please try again.`;
  const uploadUnavailableError = tq`Photo upload is temporarily unavailable. Please refresh the page and try again.`;

  // Collect per-file errors from onFileUploadFailed so the complete-failure
  // path in onUploadDone can include the actual error reasons in its NR report.
  // Filestack fires onFileUploadFailed per file *during* upload, then
  // onUploadDone fires once at the end with only metadata (no error objects).
  const fileUploadErrorsRef = useRef<Array<{ message: string }>>([]);

  // Tracks only the session belonging to the most recently opened picker, so a
  // reopen can release the previous session's pending close report. Callbacks
  // never read it — each is bound to its own session (see {@link UploadSession}).
  const uploadSessionRef = useRef<UploadSession>(createUploadSession());

  // Warm the client up on mount so the picker opens instantly. Consumers that mount
  // this hook on surfaces where the picker may never be opened can defer it — `open()`
  // (via `withClient`) prepares on demand anyway, it just pays the load at click time.
  useEffect(() => {
    if (deferPreparation) return;
    prepareForUse();
  }, [deferPreparation, prepareForUse]);

  // Resolves with whether the upload was forwarded to the consumer's
  // onUploadDone (false when validation or a complete failure dropped it).
  const wrappedOnUploadDone = useEventCallback<(files: PickerResponse) => Promise<boolean>>(async files => {
    // Drain per-file errors collected during this upload session.
    const collectedFileErrors = fileUploadErrorsRef.current.splice(0);

    // Check every uploaded image (local and remote) against the pixel cap via
    // Filestack metadata before forwarding. Oversized ones are dropped here so
    // they never reach the media_service maxInputPixels path, and the user is
    // shown a single "too large" toast — uniform across all picker sources.
    const client = await withClient(filestackClient => filestackClient);
    const { result, hasOversized, shouldForward } = await processUploadedFiles(client, files);

    if (hasOversized) {
      toast(oversizedImageError, { icon: <WarningTriangle /> });
    }

    // Oversized images were dropped and the user notified; if nothing valid
    // remains and there were no genuine failures, there's nothing to forward.
    if (!shouldForward) {
      return false;
    }

    if (result.filesFailed.length) {
      const isCompleteFailure = result.filesUploaded.length === 0;
      const errorMessage = isCompleteFailure ? 'Complete filestack photo upload failure' : 'Partial filestack photo upload failure';
      addError(new Error(errorMessage), {
        context: 'useFilestack#onUploadDone',
        containerId,
        isCompleteFailure,
        totalFileCount: result.filesFailed.length + result.filesUploaded.length,
        failedFileCount: result.filesFailed.length,
        failedFiles: result.filesFailed.map(extractPickerFileMetadata),
        // Per-file error reasons captured from onFileUploadFailed during upload.
        // Joined as a flat string — NR custom attributes don't support nested arrays.
        fileErrorMessages: collectedFileErrors.map(e => e.message).join(' | ') || undefined,
        usageContext,
      });

      if (isCompleteFailure) {
        // A complete upload failure (e.g. a size-0/failed store — the CORE-6504 class) must never be
        // swallowed. Consumers may pass onCompleteUploadFailure for custom handling (e.g. reopen + retry);
        // when none is provided, surface a default error toast here so no caller can silently drop it. We
        // confirmed the gap spans multiple pickers — PagePhotoSettings' openFilePicker and PhotoPicker both
        // omit the callback — so the guarantee belongs in the shared hook, not each consumer.
        if (onCompleteUploadFailure) {
          onCompleteUploadFailure();
        } else {
          toast(uploadFailedError, { icon: <WarningTriangle /> });
        }
        return false;
      }
    }

    // Await the consumer handler so the close report (below) can't outrun it.
    // Its errors are reported instead of propagated — a throwing consumer must
    // not block the close report or surface as an unhandled rejection.
    try {
      await onUploadDone(result);
    } catch (error) {
      addError(error, { context: 'useFilestack#consumerOnUploadDone', containerId, usageContext });
    }
    return true;
  });

  const trackedOnUploadDone = useEventCallback<(session: UploadSession, files: PickerResponse) => Promise<void>>((session, files) => {
    session.didUpload = true;
    // Release a close that already fired (PRNT-4630) so it chains on the
    // processing promise below instead of reporting a false "no upload".
    settleAwaitUpload(session);
    // Captured synchronously, before wrappedOnUploadDone's first await, so a
    // close firing in the same tick still sees the in-flight processing.
    const processing = wrappedOnUploadDone(files)
      .then(didForward => {
        session.didForward = session.didForward || !!didForward;
      })
      .catch(error => {
        addError(error, { context: 'useFilestack#uploadProcessing', containerId, usageContext });
      });
    session.processing = processing;
    return processing;
  });

  const wrappedOnFileSelected = useEventCallback<(session: UploadSession, file: PickerFileMetadata) => ReturnType<PickerFileCallback>>((session, file) => {
    session.didSelectFiles = true;
    return (onFileSelected ?? defaultOnFileSelected)(file);
  });

  // Arm the close-report wait only once an upload is actually under way.
  // Arming on selection instead would also catch the ordinary "picked a file,
  // then closed the picker" cancel — where onUploadDone never comes — and delay
  // the consumer's close by the full grace period.
  const trackedOnUploadStarted = useEventCallback((session: UploadSession) => {
    armAwaitUpload(session);
  });

  // Every upload in the pick was cancelled, so no result is coming: release a
  // close report waiting on the grace period instead of stalling it.
  const trackedOnCancel = useEventCallback((session: UploadSession) => {
    settleAwaitUpload(session);
  });

  // Separate trampoline so a report deferred behind in-flight processing still
  // invokes the latest consumer `onClose` rather than the one captured when the
  // picker's raw close fired.
  const reportClose = useEventCallback((info: FilestackCloseInfo) => onClose?.(info));

  const wrappedOnClose = useEventCallback((session: UploadSession) => {
    // Info is computed after processing settles (didForward lands then), from
    // this session's own state so a concurrent session can't corrupt the report.
    const report = () => reportClose({ didSelectFiles: session.didSelectFiles, didUpload: session.didUpload, didForward: session.didForward });
    const recordReportFailure = (error: unknown) => {
      // `report` (the consumer onClose) threw. On the synchronous path below it
      // propagates to Filestack as it always has; once deferred it must not
      // become an unhandled rejection, so record it instead.
      addError(error, { context: 'useFilestack#onClose', containerId, usageContext });
    };
    const reportWhenProcessingSettles = () => {
      if (!session.processing) {
        report();
        return;
      }
      session.processing.then(report).catch(recordReportFailure);
    };

    // Nothing to wait for: report on the spot (upload already in-flight is
    // handled by chaining on `processing`).
    if (session.processing || !session.awaitUpload) {
      reportWhenProcessingSettles();
      return;
    }

    // PRNT-4630: an upload started but `onUploadDone` hasn't fired yet — the
    // picker closed before the result was delivered. Wait for it, bounded, so
    // the report reflects the real outcome rather than a false "no upload".
    const graceTimer = setTimeout(() => settleAwaitUpload(session), UPLOAD_GRACE_MS);
    session.awaitUpload.promise
      .then(() => {
        clearTimeout(graceTimer);
        reportWhenProcessingSettles();
      })
      .catch(recordReportFailure);
  });

  const wrappedOnFileUploadFailed = useEventCallback<PickerFileErrorCallback>((file, error) => {
    fileUploadErrorsRef.current.push({ message: error.message });
    addError(error, { context: 'useFilestack#onFileUploadFailed', file: extractPickerFileMetadata(file), containerId, usageContext });
    onFileUploadFailed?.(file, error);
  });

  // Built per open so every callback is bound to that picker's own session.
  const buildPickerOptions = (session: UploadSession): PickerOptions => ({
    cleanupImageExif: false,
    rootId,
    // Chose to not allow "imagesearch" because results vary in quality.
    // Instead, we'll leverage unsplash as the primary search source
    fromSources: ['local_file_system', 'url', 'unsplash', 'facebook', 'dropbox'],
    container: pickerContainer,
    maxFiles: Math.max(1, maxFiles),
    uploadInBackground: false,
    disableStorageKey: true,
    transformations,
    storeTo: {
      location: 'azure',
      container: containerId,
    },
    onFileSelected: file => wrappedOnFileSelected(session, file),
    onUploadStarted: () => trackedOnUploadStarted(session),
    onUploadDone: files => trackedOnUploadDone(session, files),
    onCancel: () => trackedOnCancel(session),
    onFileUploadFailed: wrappedOnFileUploadFailed,
    onOpen,
    onClose: () => wrappedOnClose(session),
    accept,
  });
  const open = () => {
    // Reopening abandons the previous session: release any close report still
    // waiting on its grace period so it lands now, ahead of the new session,
    // rather than interleaving with it up to UPLOAD_GRACE_MS later.
    settleAwaitUpload(uploadSessionRef.current);
    const session = createUploadSession();
    uploadSessionRef.current = session;
    const options = buildPickerOptions(session);
    withClient(client => {
      // Open operation is idempotent.
      client.picker(options).open();
    }).catch(error => {
      const chunkLoadError = isChunkLoadError(error);
      if (!chunkLoadError) {
        addError(error, { context: 'useFilestack#open', containerId, usageContext });
      }
      // Chunk failures were already reported by ChunkErrorBoundary; ordinary
      // picker failures need telemetry and the generic upload-failure message.
      toast(chunkLoadError ? uploadUnavailableError : uploadFailedError, { icon: <WarningTriangle /> });
    });
  };

  const getPhotoPreviewUrl = ({ url }: PreviewProps): Promise<string> => {
    return withClient(client => {
      return `${url}?policy=${client?.session.policy}&signature=${client?.session.signature}`;
    });
  };

  const getPhotosPreviewUrls = ({ urls }: { urls: string[] }): Promise<string[]> => {
    return withClient(client => {
      return urls.map(url => `${url}?policy=${client?.session.policy}&signature=${client?.session.signature}`);
    });
  };

  return { open, getPhotoPreviewUrl, getPhotosPreviewUrls };
};
